/**
 * Factory modal Calendario (DOM). Mutazioni tramite calendarFacade soltanto dopo validazione locale.
 */

import { isWeekLocked } from '../store.js';
import {
  SLOT_CATEGORY_LABELS,
  ACTIVITY_TYPE_LABELS,
  TIME_SLOT_LABELS,
  sortWeekdaysCalendarOrder,
  getTemplateWeekdays,
} from '../types.js';
import { getOfficialSlotsForDates } from '../shiftGenerator.js';
import {
  ensurePlannerSlotName,
  formatLocalISODate,
  getMondayOfWeek,
  getUniqueSlotName,
  addCalendarDays,
} from '../utils.js';
import { escapeHtmlText, escapeHtmlAttr } from './domSafe.js';
import {
  SHIFT_PLANNER_LOCKED_CALENDAR_MSG,
  weekdayCheckboxesHtml,
  applyCategoryToUocSelect,
  requiredUocIdForCategory,
  collectConflictingTemplateNames,
  formatTemplateWeekdaysShort,
} from './calendarGrid.js';
import { calendarCreateModification, calendarUpdateTemplate } from './calendarFacade.js';

export function showAddSlotPopup(_container, { date, timeSlot }, uocsList, onDone) {
  const weekStart = getMondayOfWeek(date);
  if (isWeekLocked(weekStart)) {
    alert(SHIFT_PLANNER_LOCKED_CALENDAR_MSG);
    return;
  }
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  const headDate = escapeHtmlText(date);
  const headTs = escapeHtmlText(TIME_SLOT_LABELS[timeSlot] || timeSlot);
  overlay.innerHTML = `
    <div class="modal">
      <div class="modal-header">
        <h3>Aggiungi slot – ${headDate} (${headTs})</h3>
        <button type="button" class="btn btn-secondary btn-sm" id="add-slot-close">×</button>
      </div>
      <div class="modal-body">
        <div style="display: flex; flex-wrap: wrap; gap: 0.75rem; margin-bottom: 1rem;">
          <div>
            <label>Categoria</label>
            <select id="add-slot-category">
              ${Object.entries(SLOT_CATEGORY_LABELS)
                .map(([k, v]) => `<option value="${escapeHtmlAttr(k)}">${escapeHtmlText(v)}</option>`)
                .join('')}
            </select>
          </div>
          <div>
            <label>UOC (derivata dalla categoria)</label>
            <select id="add-slot-uoc" disabled title="La UOC è fissata dalla categoria scelta.">
              ${uocsList
                .map((u) => `<option value="${escapeHtmlAttr(u.id)}">${escapeHtmlText(u.name)}</option>`)
                .join('')}
            </select>
          </div>
          <div>
            <label for="add-slot-name">Nome (opz.)</label>
            <input type="text" id="add-slot-name" placeholder="es. SO Mattino" autocomplete="off">
          </div>
          <div>
            <label>Tipo attività</label>
            <select id="add-slot-activity-type">
              ${Object.entries(ACTIVITY_TYPE_LABELS)
                .map(([k, v]) => `<option value="${escapeHtmlAttr(k)}">${escapeHtmlText(v)}</option>`)
                .join('')}
            </select>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" id="add-slot-cancel">Annulla</button>
        <button type="button" class="btn btn-primary" id="add-slot-save">Aggiungi</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const modalEl = overlay.querySelector('.modal');
  modalEl?.addEventListener('click', (ev) => ev.stopPropagation());
  modalEl?.addEventListener('mousedown', (ev) => ev.stopPropagation());

  const catSel = overlay.querySelector('#add-slot-category');
  const uocSel = overlay.querySelector('#add-slot-uoc');
  const nameIn = overlay.querySelector('#add-slot-name');
  const syncAddUoc = () => applyCategoryToUocSelect(catSel.value, uocSel);
  catSel.addEventListener('change', syncAddUoc);
  syncAddUoc();
  nameIn?.addEventListener('keydown', (ev) => ev.stopPropagation());

  const close = () => {
    overlay.remove();
    onDone?.();
  };

  overlay.querySelector('#add-slot-close').addEventListener('click', close);
  overlay.querySelector('#add-slot-cancel').addEventListener('click', close);
  overlay.querySelector('#add-slot-save').addEventListener('click', async () => {
    const category = catSel.value;
    const uocId = requiredUocIdForCategory(category);
    if (!uocId) {
      alert('Categoria senza UOC mappata: aggiorna l’app o scegli un’altra categoria.');
      return;
    }
    const rawName = nameIn.value.trim();
    const activityType = overlay.querySelector('#add-slot-activity-type').value;
    const baseLabel = ensurePlannerSlotName(category, rawName, timeSlot, activityType);
    const existingNames = getOfficialSlotsForDates([date])
      .filter((s) => s.date === date && s.timeSlot === timeSlot)
      .map((s) => ensurePlannerSlotName(s.category, s.name, s.timeSlot, s.activityType || 'clinico'))
      .filter(Boolean);
    const name = getUniqueSlotName(baseLabel, existingNames) || baseLabel;
    if (isWeekLocked(getMondayOfWeek(date))) {
      alert(SHIFT_PLANNER_LOCKED_CALENDAR_MSG);
      return;
    }
    try {
      await calendarCreateModification({
        modificationType: 'add',
        scope: 'one_off',
        dateSpecific: date,
        dateFrom: date,
        timeSlot,
        category,
        uocId,
        name,
        activityType,
      });
      close();
    } catch (e) {
      alert('Errore: ' + e.message);
    }
  });
  let closeAddSlotOnOverlayPointer = false;
  overlay.addEventListener('mousedown', (e) => {
    closeAddSlotOnOverlayPointer = e.target === overlay;
  });
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay && closeAddSlotOnOverlayPointer) close();
    closeAddSlotOnOverlayPointer = false;
  });
  // Autofocus del campo Nome: senza, su touch/kiosk il tap non porta il focus in modo affidabile
  // (allineato al modal "Aggiungi slot" del Piano turni, che così funziona).
  requestAnimationFrame(() => {
    try { nameIn?.focus(); } catch (_) {}
  });
}

export function showCessationPopup(_container, template, onDone) {
  const wdLabel = formatTemplateWeekdaysShort(template);
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  const titleLine = escapeHtmlText((template.name || '').trim()) || escapeHtmlText(template.category || '');
  const strongLine =
    escapeHtmlText((template.name || '').trim()) || escapeHtmlText('—');

  overlay.innerHTML = `
    <div class="modal">
      <div class="modal-header">
        <h3>Cessa slot: ${titleLine || '—'}</h3>
        <button type="button" class="btn btn-secondary btn-sm" id="cessa-close">×</button>
      </div>
      <div class="modal-body">
        <p style="font-size: 0.9rem; color: var(--text-secondary); margin-bottom: 1rem;">
          Slot <strong>${strongLine}</strong> — ${escapeHtmlText(wdLabel)} — ${escapeHtmlText(
    TIME_SLOT_LABELS[template.timeSlot] || template.timeSlot || '',
  )}.<br>
          Indica il <strong>primo giorno in cui lo slot non deve più comparire</strong> (escluso). La data di fine attività inclusiva sul template sarà impostata al giorno precedente.
        </p>
        <div>
          <label>Non più attivo da (primo giorno escluso)</label>
          <input type="date" id="cessa-first-excluded" style="display:block;margin-top:0.35rem;">
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" id="cessa-cancel">Annulla</button>
        <button type="button" class="btn btn-primary" id="cessa-confirm">Applica fine attività</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const defaultDate = formatLocalISODate(new Date());
  const dateIn = overlay.querySelector('#cessa-first-excluded');
  if (dateIn) dateIn.value = defaultDate.slice(0, 10);

  const close = () => {
    overlay.remove();
    onDone?.();
  };

  overlay.querySelector('#cessa-close').addEventListener('click', close);
  overlay.querySelector('#cessa-cancel').addEventListener('click', close);
  overlay.querySelector('#cessa-confirm').addEventListener('click', async () => {
    const firstExcluded = overlay.querySelector('#cessa-first-excluded').value;
    if (!firstExcluded) {
      alert('Inserisci una data.');
      return;
    }
    const newEnd = addCalendarDays(firstExcluded, -1);
    const prev = template.dateTo || null;
    let merged = newEnd;
    if (prev && prev < newEnd) merged = prev;
    if (template.dateFrom && merged < template.dateFrom) {
      alert('La data è precedente all’inizio attività del template. Controlla le date.');
      return;
    }
    try {
      await calendarUpdateTemplate(template.id, { dateTo: merged });
      close();
    } catch (e) {
      alert('Errore: ' + e.message);
    }
  });
  let cessPointer = false;
  overlay.addEventListener('mousedown', (e) => {
    cessPointer = e.target === overlay;
  });
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay && cessPointer) close();
    cessPointer = false;
  });
}

export function showEditTemplatePopup(_container, template, uocsList, onDone) {
  const twSet = new Set(getTemplateWeekdays(template));
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal">
      <div class="modal-header">
        <h3>Modifica slot ricorrente</h3>
        <button type="button" class="btn btn-secondary btn-sm" id="edit-tpl-close">×</button>
      </div>
      <div class="modal-body">
        <div style="display: flex; flex-wrap: wrap; gap: 0.75rem; margin-bottom: 1rem;">
          <div>
            <label for="edit-tpl-name">Nome attività</label>
            <input type="text" id="edit-tpl-name" placeholder="es. TI Mattino" autocomplete="off">
          </div>
          <div>
            <label>Tipo attività</label>
            <select id="edit-tpl-activity-type">
              ${Object.entries(ACTIVITY_TYPE_LABELS)
                .map(
                  ([k, v]) =>
                    `<option value="${escapeHtmlAttr(k)}" ${(template.activityType || 'clinico') === k ? 'selected' : ''}>${escapeHtmlText(v)}</option>`,
                )
                .join('')}
            </select>
          </div>
          <div>
            <label>Categoria</label>
            <select id="edit-tpl-category">
              ${Object.entries(SLOT_CATEGORY_LABELS)
                .map(
                  ([k, v]) =>
                    `<option value="${escapeHtmlAttr(k)}" ${template.category === k ? 'selected' : ''}>${escapeHtmlText(v)}</option>`,
                )
                .join('')}
            </select>
          </div>
          <div>
            <label>UOC (derivata dalla categoria)</label>
            <select id="edit-tpl-uoc" disabled title="La UOC è fissata dalla categoria scelta.">
              ${uocsList
                .map(
                  (u) =>
                    `<option value="${escapeHtmlAttr(u.id)}" ${template.uocId === u.id ? 'selected' : ''}>${escapeHtmlText(u.name)}</option>`,
                )
                .join('')}
            </select>
          </div>
          <div style="flex: 1 1 100%;">
            <label style="display:block;margin-bottom:0.35rem;">Giorni della settimana</label>
            <div class="template-wd-row" style="display:flex;flex-wrap:wrap;align-items:center;">${weekdayCheckboxesHtml('edit-tpl-wd', twSet)}</div>
          </div>
          <div>
            <label>Attivo dal (opz.)</label>
            <input type="date" id="edit-tpl-date-from" value="${escapeHtmlAttr((template.dateFrom || '').slice(0, 10))}">
          </div>
          <div>
            <label>Fino al incluso (opz.)</label>
            <input type="date" id="edit-tpl-date-to" value="${escapeHtmlAttr((template.dateTo || '').slice(0, 10))}">
          </div>
          <div>
            <label>Fascia</label>
            <select id="edit-tpl-timeslot">
              ${Object.entries(TIME_SLOT_LABELS)
                .map(
                  ([k, v]) =>
                    `<option value="${escapeHtmlAttr(k)}" ${template.timeSlot === k ? 'selected' : ''}>${escapeHtmlText(v)}</option>`,
                )
                .join('')}
            </select>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" id="edit-tpl-cancel">Annulla</button>
        <button type="button" class="btn btn-primary" id="edit-tpl-save">Salva</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const nameEl = overlay.querySelector('#edit-tpl-name');
  if (nameEl) nameEl.value = template.name || '';

  const editCat = overlay.querySelector('#edit-tpl-category');
  const editUoc = overlay.querySelector('#edit-tpl-uoc');
  const syncEditUoc = () => applyCategoryToUocSelect(editCat.value, editUoc);
  editCat.addEventListener('change', syncEditUoc);
  syncEditUoc();

  const close = () => {
    overlay.remove();
    onDone?.();
  };

  const modalEl = overlay.querySelector('.modal');
  modalEl?.addEventListener('click', (ev) => ev.stopPropagation());
  modalEl?.addEventListener('mousedown', (ev) => ev.stopPropagation());

  overlay.querySelector('#edit-tpl-close').addEventListener('click', close);
  overlay.querySelector('#edit-tpl-cancel').addEventListener('click', close);
  overlay.querySelector('#edit-tpl-save').addEventListener('click', async () => {
    const wds = sortWeekdaysCalendarOrder([...overlay.querySelectorAll('.edit-tpl-wd:checked')].map((el) => el.value));
    if (!wds.length) {
      alert('Seleziona almeno un giorno della settimana.');
      return;
    }
    const df = overlay.querySelector('#edit-tpl-date-from').value?.trim() || null;
    const dto = overlay.querySelector('#edit-tpl-date-to').value?.trim() || null;
    if (df && dto && dto < df) {
      alert('La data fine non può precedere la data di inizio.');
      return;
    }
    const category = editCat.value;
    const uocId = requiredUocIdForCategory(category);
    if (!uocId) {
      alert('Categoria senza UOC mappata: aggiorna l’app o scegli un’altra categoria.');
      return;
    }
    const timeSlot = overlay.querySelector('#edit-tpl-timeslot').value;
    const activityType = overlay.querySelector('#edit-tpl-activity-type').value;
    const rawName = overlay.querySelector('#edit-tpl-name').value.trim();
    const baseLabel = ensurePlannerSlotName(category, rawName, timeSlot, activityType);
    const existingNames = collectConflictingTemplateNames(timeSlot, wds, df, dto, template.id);
    const uniqueName = getUniqueSlotName(baseLabel, existingNames) || baseLabel;
    const t = {
      name: uniqueName,
      activityType,
      uocId,
      weekdays: wds,
      weekday: wds[0],
      timeSlot,
      category,
      dateFrom: df,
      dateTo: dto,
    };
    try {
      await calendarUpdateTemplate(template.id, t);
      close();
    } catch (e) {
      alert('Errore: ' + e.message);
    }
  });
  let editPointer = false;
  overlay.addEventListener('mousedown', (e) => {
    editPointer = e.target === overlay;
  });
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay && editPointer) close();
    editPointer = false;
  });
}
